import { useCallback, useEffect, useLayoutEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { CUSTOM_EVENTS } from '~/common/constants/events';
import {
  DEFAULT_COUNTRY_CODE,
  DEFAULT_REGION,
  type Region,
  type RegionInfo,
} from '~/common/constants/region';
import { isValidRegion } from '~/common/utils/region';
import { isBrowser } from '~/common/utils/browser';
import { CookieService } from '~/common/utils/cookies';
import { useRegionsEnabled } from '~/common/hooks/useRegionsEnabled';

function getInitialRegionInfo(): RegionInfo {
  if (!isBrowser) {
    return { region: DEFAULT_REGION, countryCode: DEFAULT_COUNTRY_CODE };
  }

  const stored = CookieService.getRegionInfo();
  if (stored) return stored;

  const serverRegion = window.ENV?.RESOLVED_REGION;
  const serverCountry = window.ENV?.RESOLVED_COUNTRY_CODE;

  if (isValidRegion(serverRegion) && serverCountry) {
    return { region: serverRegion, countryCode: serverCountry };
  }

  return { region: DEFAULT_REGION, countryCode: DEFAULT_COUNTRY_CODE };
}

const DEFAULT_REGION_INFO: RegionInfo = {
  region: DEFAULT_REGION,
  countryCode: DEFAULT_COUNTRY_CODE,
};

export const regionInfoState = atom<RegionInfo>({
  key: 'regionInfo',
  default: DEFAULT_REGION_INFO,
});

export const regionModalOpenState = atom<boolean>({
  key: 'regionModalOpen',
  default: false,
});

export function getRegionInfoFromEnv(rootLoaderEnv: {
  RESOLVED_REGION?: string;
  RESOLVED_COUNTRY_CODE?: string;
}): RegionInfo {
  const serverRegion = rootLoaderEnv.RESOLVED_REGION;
  const serverCountry = rootLoaderEnv.RESOLVED_COUNTRY_CODE;

  if (isValidRegion(serverRegion) && serverCountry) {
    return { region: serverRegion, countryCode: serverCountry.toUpperCase() };
  }

  return DEFAULT_REGION_INFO;
}

export function useRegion() {
  const [regionInfo, setRegionInfoState] = useRecoilState(regionInfoState);
  const [, setRegionModalOpen] = useRecoilState(regionModalOpenState);
  const regionsEnabled = useRegionsEnabled();

  useLayoutEffect(() => {
    const stored = CookieService.getRegionInfo();

    if (stored) {
      setRegionInfoState(stored);
      CookieService.setRegionInfo(stored);
      return;
    }

    const serverRegion = window.ENV?.RESOLVED_REGION;
    const serverCountry = window.ENV?.RESOLVED_COUNTRY_CODE;
    const fromIP = isValidRegion(serverRegion) && !!serverCountry;

    const initial: RegionInfo = fromIP
      ? { region: serverRegion as Region, countryCode: serverCountry as string }
      : { region: DEFAULT_REGION, countryCode: DEFAULT_COUNTRY_CODE };

    setRegionInfoState(initial);
    CookieService.setRegionInfo(initial);

    if (fromIP && regionsEnabled) {
      setRegionModalOpen(true);
    }
  }, [regionsEnabled, setRegionInfoState, setRegionModalOpen]);

  useEffect(() => {
    const handleConsentChange = () => {
      if (CookieService.hasFunctionalityConsent())
        CookieService.migrateRegionToCookie();
      else CookieService.clearRegionCookies();

      const next = getInitialRegionInfo();
      setRegionInfoState(next);
      CookieService.setRegionInfo(next);
    };

    window.addEventListener(
      CUSTOM_EVENTS.cookieConsentUpdated,
      handleConsentChange,
    );
    return () => {
      window.removeEventListener(
        CUSTOM_EVENTS.cookieConsentUpdated,
        handleConsentChange,
      );
    };
  }, [setRegionInfoState]);

  const setRegion = useCallback(
    (newInfo: RegionInfo) => {
      setRegionInfoState(newInfo);
      CookieService.setRegionInfo(newInfo);
      window.dispatchEvent(new Event(CUSTOM_EVENTS.regionChanged));
    },
    [setRegionInfoState],
  );

  return {
    region: regionInfo.region,
    countryCode: regionInfo.countryCode,
    setRegion,
  };
}
